/**
 * WCC360 v2 — causes and hobby frequency.
 *
 * Two additions to the "What lights you up" chapter, both of which exist to
 * fix the same weakness: a tap is a tap. Before this, "I care about climate
 * change" and "I make reels" carried identical weight, and a passion someone
 * touches once a year counted the same as one they do every single day.
 *
 *   1. HOBBY FREQUENCY (`px_hobby_frequency`, X-HOBBY-002). For each
 *      Gen-Z-style activity the learner picked, we ask how often they
 *      actually do it. This is a behavioural fact, not a virtue — "I hardly
 *      ever do it any more" has no socially correct answer — so it is safe
 *      to ask directly. It scales the family-affinity that pick already
 *      carried; it never creates new affinity and never gates anything.
 *
 *   2. CAUSE RANKING (`px_cause_rank`, X-CAUSE-001). The learner orders
 *      their top three global issues. Forced ranking is the point: everyone
 *      says they care about all fourteen, so an unranked list discriminates
 *      almost nothing. A rank is a real trade-off.
 *
 * SCORING RULES (both bounded, both honest):
 *   - Frequency is a MULTIPLIER on an existing affinity count, in the range
 *     [0.4, 1.5]. It can strengthen or soften what the learner already told
 *     us. It cannot add a family they never named.
 *   - A ranked cause contributes affinity through the same channel passions
 *     use, weighted 1.5 / 1.2 / 1.0 by position.
 *   - Neither is ever read by the eligibility gate. A cause must never
 *     delete a career from the pool.
 *
 * CANON-PENDING: X-HOBBY-002 and X-CAUSE-001 are not yet ratified in the
 * Item Bank. Recorded here until the Command Center carries them.
 */
import { PASSIONS, type Passion } from "@/data/passions";
import { LEGACY_CAUSE_ALIASES, NEW_CAUSE_NEXT_STEPS } from "./causeCatalogue";

export const CAUSES_CATEGORY = "The World You Want To Fix";
export const HOBBIES_CATEGORY = "How You Spend Your Own Time";

/** The fourteen global issues, straight from the passion catalogue. */
export const CAUSES: Passion[] = PASSIONS.filter((p) => p.category === CAUSES_CATEGORY);
/** The Gen-Z activity set, straight from the passion catalogue. */
export const HOBBIES: Passion[] = PASSIONS.filter((p) => p.category === HOBBIES_CATEGORY);

const CAUSE_IDS = new Set(CAUSES.map((c) => c.id));
const HOBBY_IDS = new Set(HOBBIES.map((h) => h.id));

/** Folds retired cause ids onto the closest current area, so saved runs still resolve. */
export const resolveCauseId = (id: string): string => LEGACY_CAUSE_ALIASES[id] ?? id;

export const isCauseId = (id: string): boolean => CAUSE_IDS.has(resolveCauseId(id));
export const isHobbyId = (id: string): boolean => HOBBY_IDS.has(id);

export const MAX_RANKED_CAUSES = 3;

// ---- frequency ----------------------------------------------------------

export type HobbyFrequency = "daily" | "weekly" | "monthly" | "rarely";

export const HOBBY_FREQUENCY_OPTIONS: Array<{
  id: HobbyFrequency; label: string; hint: string;
}> = [
  { id: "daily",   label: "Most days",        hint: "It's part of your week, every week" },
  { id: "weekly",  label: "Every week or so", hint: "You come back to it regularly" },
  { id: "monthly", label: "Once in a while",  hint: "A few times a month, when you get the chance" },
  { id: "rarely",  label: "Hardly ever now",  hint: "You love it, but life got in the way" },
];

/**
 * Multiplier applied to the family affinity that pick already carried.
 * Bounded on both sides: doing something daily cannot manufacture a match,
 * and "hardly ever" softens the signal without erasing it — someone can love
 * a thing they've had no time for.
 */
export const HOBBY_FREQUENCY_WEIGHT: Record<HobbyFrequency, number> = {
  daily: 1.5,
  weekly: 1.2,
  monthly: 0.8,
  rarely: 0.4,
};

/** Second-person phrasing, used verbatim in "Why you matched". */
export const HOBBY_FREQUENCY_PHRASE: Record<HobbyFrequency, string> = {
  daily: "most days",
  weekly: "most weeks",
  monthly: "now and then",
  rarely: "rarely these days",
};

/** Weight for a cause by its position in the learner's top three. */
export const CAUSE_RANK_WEIGHT = [1.5, 1.2, 1.0];
/** A cause they named as a passion but did NOT rank. Still counts, less. */
export const UNRANKED_CAUSE_WEIGHT = 0.8;

export function causeRankWeight(index: number): number {
  return CAUSE_RANK_WEIGHT[index] ?? UNRANKED_CAUSE_WEIGHT;
}

export const ORDINAL = ["first", "second", "third"];

// ---- weighted affinity ---------------------------------------------------

export interface LightsYouUpAnswers {
  passion_ids?: string[];
  hobby_frequency?: Record<string, HobbyFrequency>;
  cause_rank_ids?: string[];
}

function multiplierFor(id: string, a: LightsYouUpAnswers): number {
  if (isHobbyId(id)) {
    const f = a.hobby_frequency?.[id];
    // Unanswered frequency leaves the pick exactly as strong as it was.
    return f ? HOBBY_FREQUENCY_WEIGHT[f] : 1;
  }
  if (isCauseId(id)) {
    const rid = resolveCauseId(id);
    const idx = a.cause_rank_ids?.findIndex((x) => resolveCauseId(x) === rid) ?? -1;
    return idx >= 0 ? causeRankWeight(idx) : UNRANKED_CAUSE_WEIGHT;
  }
  return 1;
}

/**
 * Family affinity from the learner's picks, scaled by how often they do the
 * thing and how highly they ranked the cause. Same shape as
 * `passionFamilyCounts` — a family → strength map read by `scorePassion` —
 * so nothing new enters the six scoring slots.
 */
export function weightedPassionFamilyCounts(
  ids: string[] | undefined,
  answers: LightsYouUpAnswers,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!ids) return out;
  for (const id of ids) {
    const p = PASSIONS.find((x) => x.id === resolveCauseId(id));
    if (!p) continue;
    const w = multiplierFor(id, answers);
    for (const f of p.families) out.set(f, (out.get(f) ?? 0) + w);
  }
  return out;
}

/**
 * Affinity from ranked causes. Deliberately separate from the passion picks:
 * a learner can rank climate first without ever having tapped it on the
 * passions screen, and that priority should still shape what they see.
 */
export function causeFamilyCounts(rankIds: string[] | undefined): Map<string, number> {
  const out = new Map<string, number>();
  if (!rankIds) return out;
  rankIds.slice(0, MAX_RANKED_CAUSES).forEach((id, i) => {
    const c = causeById(id);
    if (!c) return;
    const w = causeRankWeight(i);
    for (const f of c.families) out.set(f, (out.get(f) ?? 0) + w);
  });
  return out;
}

// ---- explainability -----------------------------------------------------

/** family → the ranked cause that points at it, strongest rank winning. */
export function causeFamilyLabels(
  rankIds: string[] | undefined,
): Map<string, { label: string; ordinal: string }> {
  const out = new Map<string, { label: string; ordinal: string }>();
  (rankIds ?? []).slice(0, MAX_RANKED_CAUSES).forEach((id, i) => {
    const c = causeById(id);
    if (!c) return;
    for (const f of c.families) {
      if (!out.has(f)) out.set(f, { label: c.label, ordinal: ORDINAL[i] ?? "top" });
    }
  });
  return out;
}

/** family → the hobby that points at it, plus how often they do it. */
export function hobbyFamilyLabels(
  ids: string[] | undefined,
  freq: Record<string, HobbyFrequency> | undefined,
): Map<string, { label: string; phrase: string }> {
  const out = new Map<string, { label: string; phrase: string }>();
  for (const id of ids ?? []) {
    if (!isHobbyId(id)) continue;
    const h = HOBBIES.find((x) => x.id === id);
    const f = freq?.[id];
    // Only spoken about when they told us it's a live habit. Claiming a
    // match on something someone hardly does any more reads as a lie.
    if (!h || !f || f === "rarely") continue;
    for (const fam of h.families) {
      if (!out.has(fam)) out.set(fam, { label: h.label, phrase: HOBBY_FREQUENCY_PHRASE[f] });
    }
  }
  return out;
}

// ---- next steps ---------------------------------------------------------

/** One rung of the route in. Small enough to finish, real enough to prove. */
export interface CauseMilestone {
  /** Four words or fewer. This is the thing they tell someone they did. */
  title: string;
  /** What they actually do. Cheapest credible version, always. */
  detail: string;
  /** How they know it's done. A thing that exists afterwards, not a feeling. */
  checkpoint: string;
  /** Rough window, so it reads as a path and not a wish. */
  window: string;
  /** Honest cost signal. Never a number — costs move, and we don't invent them. */
  cost: "Free" | "Low cost" | "Paid route";
}

/** The under-30-minute doorway. Doable this week, from a phone, for nothing. */
export interface CauseQuickWin {
  title: string;
  /** Never above 30. Enforced by test. */
  minutes: number;
  /** Two or three micro-moves. Each one a single action. */
  steps: string[];
  /** What exists at the end of the 30 minutes. */
  proof: string;
}

export interface CauseNextStep {
  /** Scroll-stopper. Short, spoken, a bit rude. */
  hook: string;
  /** Why this cause turns into paid work at all. Second person. */
  why: string;
  /** The cultural hook — the thing they've already seen, watched or scrolled. */
  culture: string;
  /** 3–5 milestones, cheapest-first. Never a single gated degree. */
  milestones: CauseMilestone[];
  /** The 30-minute doorway. */
  quickWin: CauseQuickWin;
  /** The bigger first action, still doable this month, from home, for free. */
  firstAction: string;
}

/**
 * One route per cause. Rules these follow:
 *   - The first rung is free and open to a 13-year-old.
 *   - Every milestone has a checkpoint you could show someone.
 *   - The quick win is under 30 minutes and needs nothing but a phone.
 *   - No salary, demand or placement numbers — those come from the market
 *     layer at runtime, never from copy.
 *   - Cultural references are load-bearing, not decoration: they name the
 *     thing the learner has already seen, so the work stops feeling foreign.
 */
const BASE_CAUSE_NEXT_STEPS: Record<string, CauseNextStep> = {
  climate: {
    hook: "Everyone posts about it. Almost nobody can measure it. That gap is the job.",
    why: "Cutting emissions is now an engineering, data and policy job — someone has to measure it, build the replacement and get it approved.",
    culture: "Delhi checks the AQI before deciding whether to go out. Someone built that number, and someone gets paid to make it fall.",
    milestones: [
      {
        title: "Learn the numbers",
        detail: "One free open climate-science or carbon-accounting course — NPTEL, edX or a Coursera audit track. You only need the first three modules to talk sense.",
        checkpoint: "You can explain the difference between a tonne of CO2 and a kilowatt-hour without looking it up.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Count one real thing",
        detail: "Take a month of electricity bills — home, school, your uncle's shop — and turn them into a spreadsheet with a chart.",
        checkpoint: "A one-page chart of a real building's usage, with the two biggest hogs circled.",
        window: "Month 1",
        cost: "Free",
      },
      {
        title: "Make data do work",
        detail: "Spreadsheets to real data: energy or emissions analysis. Free public datasets exist for every Indian state.",
        checkpoint: "A before-and-after estimate someone could act on, with your assumptions written down.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Get it counted",
        detail: "Environmental engineering, energy systems or climate policy — or a sustainability certificate if you're already working.",
        checkpoint: "You're enrolled, and you've named the exact role you're aiming at.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Read your own meter",
      minutes: 20,
      steps: [
        "Photograph your electricity meter now, and again in 24 hours.",
        "Subtract. That's a day of your household, in units.",
        "Multiply by 30 and check it against last month's bill.",
      ],
      proof: "You know your home's real monthly load — a number most adults can't tell you.",
    },
    firstAction: "Pick one building you use — home, school, office — and measure a month of its electricity use. Write down two changes that would cut it.",
  },
  conservation: {
    hook: "Attenborough narrates it. Someone has to go count it.",
    why: "Protecting species is fieldwork, science and hard negotiation with people who want that land for something else.",
    culture: "Every wildlife documentary you've binged rests on years of someone's field notebook. That notebook is the entry-level job.",
    milestones: [
      {
        title: "Learn to look",
        detail: "A free open ecology or wildlife-biology course, plus one species-ID app used seriously — eBird or iNaturalist.",
        checkpoint: "You can name twenty species within two kilometres of your house.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Keep a season",
        detail: "One patch, same route, every week. Boring is the method — that's what makes it data.",
        checkpoint: "Twelve weeks of logged sightings, uploaded and public.",
        window: "Months 1–3",
        cost: "Free",
      },
      {
        title: "Add a field skill",
        detail: "Survey methods, GIS mapping or camera-trap data handling. Free QGIS tutorials cover the mapping.",
        checkpoint: "A map you made yourself, showing where the animals actually are.",
        window: "Months 3–6",
        cost: "Free",
      },
      {
        title: "Get inside it",
        detail: "Zoology, forestry or wildlife biology — or a traineeship with a conservation organisation, which often takes evidence over marks.",
        checkpoint: "Your logged season is in an application, and you've sent it.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Become a data source",
      minutes: 25,
      steps: [
        "Install eBird or iNaturalist.",
        "Sit outside for fifteen minutes and log everything alive you can see.",
        "Upload it. It's now in a database researchers use.",
      ],
      proof: "Your first real observation record, public and citable.",
    },
    firstAction: "Log every bird or animal you see in one local patch for four weeks on a citizen-science app. That record is real data, and it's yours.",
  },
  oceans: {
    hook: "The Ocean Cleanup gets the reels. Water testing gets the salary.",
    why: "Water is infrastructure. It needs engineers, chemists and people who can hold polluters to a standard.",
    culture: "Chennai ran dry in 2019 and Mumbai floods most Julys. Same subject, opposite symptom, one profession.",
    milestones: [
      {
        title: "Understand the system",
        detail: "A free open course in water quality or marine science. Learn where your own tap water starts.",
        checkpoint: "You can draw your city's water journey, source to drain, on one page.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Test something",
        detail: "Basic water-testing — pH and turbidity strips cost less than a movie ticket.",
        checkpoint: "Readings from three different sources near you, dated and recorded.",
        window: "Month 1",
        cost: "Low cost",
      },
      {
        title: "Find who's accountable",
        detail: "Every water body has a legal owner. Pollution-control boards publish reports and take complaints.",
        checkpoint: "You've named the office responsible for one polluted place, and read one of their reports.",
        window: "Months 2–3",
        cost: "Free",
      },
      {
        title: "Train properly",
        detail: "Environmental or civil engineering, marine science, or a water-utility apprenticeship.",
        checkpoint: "Applied, with your testing log attached.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Find your water's origin",
      minutes: 20,
      steps: [
        "Search which reservoir, river or aquifer supplies your street.",
        "Find its current level or quality report online.",
        "Write three sentences on what happens if it drops.",
      ],
      proof: "You can answer a question most people in your city can't.",
    },
    firstAction: "Test or research the water quality of the nearest river, lake or tap supply, and find out who is legally responsible for it.",
  },
  cleanenergy: {
    hook: "Rooftops are the new real estate. Learn to read one.",
    why: "Solar, wind and batteries are being built right now, and the shortage is hands and design brains, not enthusiasm.",
    culture: "Formula E is an ad for battery engineering. Every EV on your street is a job that didn't exist when your parents were your age.",
    milestones: [
      {
        title: "Fundamentals, free",
        detail: "An open renewable-energy fundamentals course. Watts, panels, inverters, storage — that's the vocabulary.",
        checkpoint: "You can explain why a 3kW system doesn't make 3kW all day.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Design a real roof",
        detail: "Measure a roof you can stand on. Size the array, estimate output, calculate payback.",
        checkpoint: "A one-page proposal with numbers you can defend.",
        window: "Month 1",
        cost: "Free",
      },
      {
        title: "Get certified hands",
        detail: "A solar PV installer or electrical-safety certificate — short, cheap, and immediately employable.",
        checkpoint: "Certificate in hand, and you've been on one real install.",
        window: "Months 3–6",
        cost: "Low cost",
      },
      {
        title: "Go deeper",
        detail: "Electrical or energy engineering, or a grid and battery specialisation.",
        checkpoint: "Enrolled, with install experience already on your CV.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Price your own roof",
      minutes: 25,
      steps: [
        "Measure your roof's usable area in square metres, roughly.",
        "Use a free online solar calculator for your pincode.",
        "Note the system size, yearly output and payback period.",
      ],
      proof: "A costed solar estimate for a real building, made by you.",
    },
    firstAction: "Size a solar system for your own roof on paper: panels, output, payback. Show it to someone who'd have to pay for it.",
  },
  healthaccess: {
    hook: "The medicine exists. Getting it to the person is the unsolved part.",
    why: "Care that doesn't reach people isn't care. That gap is staffed by clinicians, logisticians and public-health people.",
    culture: "Aravind Eye Hospital treats millions by copying a fast-food assembly line. That's not medicine, that's systems design.",
    milestones: [
      {
        title: "Learn the gap",
        detail: "A free open public-health or community-health-worker course.",
        checkpoint: "You can name the three biggest reasons people near you don't get treated in time.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Be useful",
        detail: "Certified first aid and emergency response. A weekend, and you stop being a bystander.",
        checkpoint: "A valid first-aid certificate.",
        window: "Month 2",
        cost: "Low cost",
      },
      {
        title: "See it up close",
        detail: "Volunteer at a community health camp or clinic. Watch the queue, not the doctor.",
        checkpoint: "A written list of every point where someone nearly dropped out of care.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Pick your lane",
        detail: "Nursing, allied health, public health, or health-systems management. Allied health is the fastest credible route in.",
        checkpoint: "Applied to a named programme, for a named reason.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Map the nearest care",
      minutes: 20,
      steps: [
        "Find the closest public health centre to your home on a map.",
        "Note its distance, opening hours and what it can't treat.",
        "Work out where someone would have to go at 2am.",
      ],
      proof: "You've mapped a real access gap in your own neighbourhood.",
    },
    firstAction: "Volunteer one weekend at a community health camp or clinic and write down every reason someone nearly didn't get seen.",
  },
  pandemics: {
    hook: "You lived through the case-curve. Now learn to draw one.",
    why: "Vaccines and outbreak response are lab work plus data plus logistics, and all three are hiring.",
    culture: "In 2020 the whole planet learned to read a graph. Epidemiology is that graph, drawn early enough to matter.",
    milestones: [
      {
        title: "The science, free",
        detail: "An open epidemiology or immunology course. Transmission, R-number, immunity — the actual mechanics.",
        checkpoint: "You can explain R0 to a ten-year-old.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Count under pressure",
        detail: "Statistics plus one data tool, properly. Outbreak work is counting, fast, while people argue with you.",
        checkpoint: "You've plotted a real published outbreak dataset yourself.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Explain it to humans",
        detail: "The best outbreak people translate. Write up your chart for someone with no science background.",
        checkpoint: "A five-sentence explainer a non-scientist understood first time.",
        window: "Month 4",
        cost: "Free",
      },
      {
        title: "Get into a lab",
        detail: "Microbiology, biotechnology, pharmacy — or a research assistantship, which will take you earlier than you think.",
        checkpoint: "You've emailed three researchers whose work you actually read.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Draw one curve",
      minutes: 30,
      steps: [
        "Download any open outbreak dataset — WHO and Our World in Data are free.",
        "Plot cases over time in a spreadsheet.",
        "Mark the day it turned, and find out what happened that week.",
      ],
      proof: "A chart you made from raw data, with the turning point explained.",
    },
    firstAction: "Take one real outbreak dataset that's published openly, plot the curve yourself, and explain it in five sentences to someone who isn't scientific.",
  },
  spaceexploration: {
    hook: "Chandrayaan-3 landed. The hiring didn't stop with the rocket.",
    why: "Space is now satellites and earth observation far more than rockets, and that work is bought and sold every day.",
    culture: "Interstellar got the physics consultant. ISRO got the landing. Both needed people who could do the maths on a Tuesday.",
    milestones: [
      {
        title: "Free orbital basics",
        detail: "An open orbital-mechanics or remote-sensing course. Remote sensing is the one that's hiring.",
        checkpoint: "You can explain what a satellite actually sees, and how often.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Touch real imagery",
        detail: "Python plus open satellite data. Sentinel and Landsat imagery are free to anyone with an internet connection.",
        checkpoint: "You've opened a satellite image of your own district in software.",
        window: "Months 2–3",
        cost: "Free",
      },
      {
        title: "Prove a change",
        detail: "Map one thing that changed over five years — a lake shrinking, a forest edge, a new flyover.",
        checkpoint: "A before-and-after map with your method written underneath.",
        window: "Months 3–5",
        cost: "Free",
      },
      {
        title: "Formal route",
        detail: "Aerospace, electronics or physics — or an internship with a space agency or a private launch company.",
        checkpoint: "Your map is in a portfolio, attached to an application.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "See your street from orbit",
      minutes: 20,
      steps: [
        "Open the free Sentinel Hub or NASA Worldview browser.",
        "Find your own town and switch between this year and five years ago.",
        "Screenshot one thing that changed.",
      ],
      proof: "Your first earth-observation comparison, made from public satellite data.",
    },
    firstAction: "Download free satellite imagery of your own district and map one change over five years — a lake, a forest edge, a built-up area.",
  },
  aifuture: {
    hook: "You already use it daily. Almost nobody your age has broken one on purpose.",
    why: "Someone builds these systems and someone decides what they're allowed to do. Both are jobs, and the second is short-staffed.",
    culture: "Oppenheimer is a film about people who built something faster than they decided what it was for. The AI version of that room is hiring right now.",
    milestones: [
      {
        title: "Learn both halves",
        detail: "One free machine-learning course and one on AI ethics or safety. Skipping the second is why so many builds get pulled.",
        checkpoint: "You can describe one real AI harm and what would have prevented it.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Train something small",
        detail: "Python and data handling until you can train, test and deliberately break your own tiny model.",
        checkpoint: "A model in a notebook, plus the three inputs that make it fail.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Ship a real tool",
        detail: "Solve one genuine annoyance in your own week. Small and used beats big and imaginary.",
        checkpoint: "One other person uses your tool without you standing next to them.",
        window: "Months 4–6",
        cost: "Free",
      },
      {
        title: "Choose builder or referee",
        detail: "Computer science and data science build it; a law or policy route governs it. Both are real careers.",
        checkpoint: "You've picked a side, on purpose, and can say why.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Break a chatbot honestly",
      minutes: 25,
      steps: [
        "Ask any AI assistant three factual questions you already know the answers to.",
        "Record every mistake and confident wrong answer.",
        "Write one line on who gets hurt if someone trusts it.",
      ],
      proof: "A short, evidenced note on model failure — the exact thinking safety teams pay for.",
    },
    firstAction: "Build one small AI tool that solves a real annoyance in your own week, then write down the three ways it could be misused.",
  },
  onlinesafety: {
    hook: "Someone in your family will get scammed this year. You could be why they don't.",
    why: "Scams, leaks and harassment are a security problem and a policy problem at the same time.",
    culture: "The Social Dilemma explained the problem. Trust and safety teams are the people employed to fix it, and nobody tells you that job exists.",
    milestones: [
      {
        title: "Free fundamentals",
        detail: "An open cybersecurity fundamentals course plus a digital-rights primer. Both halves, again.",
        checkpoint: "You can explain phishing, 2FA and a data breach in plain language.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Harden real people",
        detail: "Audit and fix the accounts of five people who aren't technical. Family counts.",
        checkpoint: "Five accounts with 2FA on and passwords no longer reused.",
        window: "Month 1",
        cost: "Free",
      },
      {
        title: "Play the game legally",
        detail: "Free legal practice ranges — TryHackMe, picoCTF — teach attack thinking without breaking any law.",
        checkpoint: "A completed beginner path, with your write-ups saved.",
        window: "Months 2–5",
        cost: "Free",
      },
      {
        title: "Certify or govern",
        detail: "A recognised entry security certificate, or platform trust-and-safety training. Cybersecurity, law, or a platform role.",
        checkpoint: "One certificate booked or one application sent.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Lock down five apps",
      minutes: 20,
      steps: [
        "Open the privacy settings of the five apps you use most.",
        "Turn on two-factor authentication wherever it's offered.",
        "Screenshot what each app said it collects about you.",
      ],
      proof: "Your own attack surface, reduced and documented.",
    },
    firstAction: "Audit the privacy and security settings of the five apps you use most, then teach one older relative to do the same.",
  },
  foodsecurity: {
    hook: "Zepto gets it to you in ten minutes. A third of it rots before that.",
    why: "Feeding people is agronomy, supply chains and cold storage — most of the loss happens after the harvest.",
    culture: "Amul turned scattered farmers into a national brand with logistics, not marketing. That model is still being copied.",
    milestones: [
      {
        title: "Learn the chain",
        detail: "A free open food-systems or agronomy course. Focus on post-harvest loss — that's where the problem lives.",
        checkpoint: "You can list the five points where food is lost between farm and plate.",
        window: "Weeks 1–4",
        cost: "Free",
      },
      {
        title: "Trace one food",
        detail: "Pick something you eat weekly and follow it backwards. Ask the vendor. They'll tell you.",
        checkpoint: "A written chain, farm to your plate, with the weak link marked.",
        window: "Month 1",
        cost: "Free",
      },
      {
        title: "Grow something",
        detail: "One edible plant, from seed, on a balcony if that's all you've got. It teaches what no course does.",
        checkpoint: "Something you grew, harvested and ate.",
        window: "Months 1–4",
        cost: "Low cost",
      },
      {
        title: "Get qualified",
        detail: "Supply-chain or food-safety certification first, then agriculture, food technology or agri-business.",
        checkpoint: "A certificate underway and one agri-business you've studied properly.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Audit your own bin",
      minutes: 15,
      steps: [
        "Photograph everything edible your household throws out for two days.",
        "Total it roughly, by weight or by cost.",
        "Name the one change that would cut it most.",
      ],
      proof: "A measured food-waste baseline for a real household.",
    },
    firstAction: "Trace one food you eat weekly from farm to your plate and find where it's wasted. Grow something edible while you do it.",
  },
  educationaccess: {
    hook: "You've explained something to a friend and watched it land. That's the whole job.",
    why: "The teaching gap is filled by teachers, but also by curriculum designers and the people who build learning tools.",
    culture: "Half of YouTube is people teaching for free and getting better at it in public. Learning design is that, done deliberately.",
    milestones: [
      {
        title: "Learn how learning works",
        detail: "A free open course in teaching fundamentals or learning design. Spacing, retrieval, misconception — the real mechanics.",
        checkpoint: "You can name three reasons a learner forgets, and what beats each.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Teach one real person",
        detail: "One child who is behind, one hour a week. Evidence beats a certificate in this field.",
        checkpoint: "Four sessions done, with before-and-after scores written down.",
        window: "Months 1–2",
        cost: "Free",
      },
      {
        title: "Make it reusable",
        detail: "Turn your best lesson into something another person could teach from — a worksheet, a slide deck, a three-minute video.",
        checkpoint: "Someone else used your material without you there.",
        window: "Months 3–5",
        cost: "Free",
      },
      {
        title: "Go pro",
        detail: "A teaching degree, an education-technology role, or a teaching fellowship. Fellowships often pay while you train.",
        checkpoint: "One fellowship or programme applied to, with your tracked results attached.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Teach one thing badly, then better",
      minutes: 25,
      steps: [
        "Explain one concept you know well to someone in two minutes.",
        "Ask them to explain it back to you.",
        "Rewrite your explanation to fix exactly what they missed.",
      ],
      proof: "Two versions of one explanation, and evidence the second works.",
    },
    firstAction: "Teach one child who is behind, one hour a week, for four weeks. Track exactly what changed.",
  },
  disasterrelief: {
    hook: "When it hits, everyone films. Three people know what to do.",
    why: "When it hits, someone coordinates, someone lifts, and someone had planned for it beforehand.",
    culture: "The Kerala floods were partly coordinated on WhatsApp and a public spreadsheet, by volunteers. Logistics is the heroism nobody puts in the trailer.",
    milestones: [
      {
        title: "Free response basics",
        detail: "An open disaster-management or humanitarian-response course. Learn the incident command structure — it's used worldwide.",
        checkpoint: "You can sketch who reports to whom in a live response.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Get certified",
        detail: "First aid, rescue, or radio and communications training. Communications is the most under-supplied and the least glamorous.",
        checkpoint: "One valid certificate.",
        window: "Months 1–3",
        cost: "Low cost",
      },
      {
        title: "Join a real list",
        detail: "Local civil defence, scouting, NDRF-linked or NGO volunteer rosters. Complete their basic training.",
        checkpoint: "You're on a call-out list with your name and skills against it.",
        window: "Months 3–6",
        cost: "Free",
      },
      {
        title: "Plan, don't just respond",
        detail: "Civil engineering, public administration, or a disaster-response agency role. The planning side prevents more than the rescue side.",
        checkpoint: "Applied, with your volunteer record attached.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Make your household's plan",
      minutes: 30,
      steps: [
        "Write down what your family does if the power, water or phones go out for 48 hours.",
        "Find your nearest shelter and hospital, and the route on foot.",
        "Put it on the fridge and send it to one person outside your city.",
      ],
      proof: "A written emergency plan that exists where people can see it.",
    },
    firstAction: "Join a local civil-defence, scouting or NGO response volunteer list and complete their basic training.",
  },
  wastecircular: {
    hook: "Thrifting is already your instinct. This is the paid version.",
    why: "Designing things to last is a design job, a materials job and a manufacturing job at once.",
    culture: "Depop hauls, sneaker restoration, repair reels — the resale economy is enormous and its designers are being hired by the brands they embarrassed.",
    milestones: [
      {
        title: "Learn the loop",
        detail: "A free open circular-economy or materials course. Learn why most recycling is actually downcycling.",
        checkpoint: "You can explain why a mixed-material product can't be recycled.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Repair three things",
        detail: "Fix or remake three items you'd have binned. Photograph every stage — the process is the portfolio.",
        checkpoint: "Three repairs, documented, posted publicly.",
        window: "Month 1",
        cost: "Low cost",
      },
      {
        title: "Redesign one",
        detail: "Take the thing that broke and sketch the version that wouldn't have. Name the material change.",
        checkpoint: "A redesign sketch with a written reason for every change.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Formal route",
        detail: "Industrial or product design, materials engineering, or a waste-management specialisation.",
        checkpoint: "A portfolio of repairs and redesigns, attached to an application.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Autopsy one broken thing",
      minutes: 25,
      steps: [
        "Find something broken you were going to throw away.",
        "Open it and photograph the part that actually failed.",
        "Write one line: designed to fail there, or just bad luck?",
      ],
      proof: "Your first teardown, with a design verdict — post it and it's portfolio piece one.",
    },
    firstAction: "Repair or remake three things you'd have thrown away, photograph the process, and post it as your first portfolio piece.",
  },
  citiesfuture: {
    hook: "Bengaluru traffic isn't fate. Somebody drew that road.",
    why: "A city that moves well was designed to. That's planning, transport engineering and data.",
    culture: "Every city with a metro map you can read hired someone to make it readable. That's a career, not a hobby.",
    milestones: [
      {
        title: "Free planning basics",
        detail: "An open urban-planning or transport-planning course. Density, land use, mode share — the levers.",
        checkpoint: "You can explain why widening a road often makes traffic worse.",
        window: "Weeks 1–3",
        cost: "Free",
      },
      {
        title: "Count a street",
        detail: "One hour, one street. Tally everyone walking, cycling, riding and driving.",
        checkpoint: "A real count, with a pie chart of who actually uses that road.",
        window: "Month 1",
        cost: "Free",
      },
      {
        title: "Map it yourself",
        detail: "Free QGIS plus OpenStreetMap. Add missing footpaths in your own area — the edits go live.",
        checkpoint: "Your edits are on the public map, used by strangers.",
        window: "Months 2–4",
        cost: "Free",
      },
      {
        title: "Redraw and train",
        detail: "Redraw that street on paper for the people your count found. Then architecture, urban planning, or civil and transport engineering.",
        checkpoint: "A before-and-after street drawing in a portfolio, plus an application in.",
        window: "Year 1",
        cost: "Paid route",
      },
    ],
    quickWin: {
      title: "Count sixty minutes of one street",
      minutes: 30,
      steps: [
        "Stand at one spot near you for half an hour.",
        "Tally walkers, cyclists, two-wheelers, cars and buses on your phone.",
        "Work out how many people each mode moved, not how many vehicles.",
      ],
      proof: "Hard evidence that the street serves the wrong group — the argument every planner starts with.",
    },
    firstAction: "Count how many people walk, cycle, ride and drive down one street near you for an hour. Redraw that street on paper.",
  },
};

export const CAUSE_NEXT_STEPS: Record<string, CauseNextStep> = {
  ...BASE_CAUSE_NEXT_STEPS,
  ...NEW_CAUSE_NEXT_STEPS,
};

export function causeNextStep(id: string): CauseNextStep | null {
  return CAUSE_NEXT_STEPS[resolveCauseId(id)] ?? null;
}

/**
 * Back-compatible flat view of the route. Anything that just wants "the
 * steps" as strings — exports, the dossier, the PDF — reads this rather
 * than knowing about milestones.
 */
export function causeLearningPath(id: string): string[] {
  const step = CAUSE_NEXT_STEPS[resolveCauseId(id)];
  if (!step) return [];
  return step.milestones.map((m) => `${m.title} — ${m.detail}`);
}

export function causeById(id: string): Passion | undefined {
  const rid = resolveCauseId(id);
  return CAUSES.find((c) => c.id === rid);
}
